import {
  isRouteErrorResponse,
  Link,
  useLocation,
  useParams,
  useRouteError,
  type ErrorResponse,
} from '@remix-run/react'
import { useTranslation } from 'react-i18next'

import { Button } from '#app/components/ui/button.tsx'
import { getErrorMessage } from '#app/utils/misc.tsx'

type StatusHandler = (info: {
  error: ErrorResponse
  params: Record<string, string | undefined>
}) => JSX.Element | null

export function GeneralErrorBoundary({
  defaultStatusHandler = ({ error }) => (
    <p>
      {error.status} {error.data}
    </p>
  ),
  statusHandlers,
  unexpectedErrorHandler = error => <p>{getErrorMessage(error)}</p>,
}: {
  defaultStatusHandler?: StatusHandler
  statusHandlers?: Record<number, StatusHandler>
  unexpectedErrorHandler?: (error: unknown) => JSX.Element | null
}) {
  const error = useRouteError()
  const params = useParams()

  if (typeof document !== 'undefined') {
    console.error(error)
  }

  return (
    <div className="container flex items-center justify-center p-20 text-h2">
      {isRouteErrorResponse(error)
        ? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
            error,
            params,
          })
        : unexpectedErrorHandler(error)}
    </div>
  )
}

export function NotFoundError() {
  const location = useLocation()
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-6 pt-20">
      <div className="flex flex-col gap-3 text-center">
        <img
          className="mb-8 w-full max-w-[900px]"
          src="/images/404.jpg"
          alt="A broken plate"
        />
        <h1 className="text-h2">{t('404.title')}</h1>
        <pre className="whitespace-pre-wrap break-all text-body-lg">
          {location.pathname}
        </pre>
      </div>
      <Button asChild size="lg">
        <Link to="/" className="mx-auto text-body-md no-underline">
          {t('404.cta')}
        </Link>
      </Button>
    </div>
  )
}
